import { useQuery, useMutation } from 'react-query'
import axios from '../../utils/axios'
import { API_BASE_URL } from '../../constants'

const fetchUserList = async () => {
  const { data } = await axios.get(`${API_BASE_URL}/person`)

  // `organisation` ({id, name, parent_id}) tells an admin of several
  // organisations which one each person belongs to.
  return data.map(({ user, organisation, ...rest }) => ({
    ...user,
    ...rest,
    organisation: organisation || null,
  }))
}

const fetchOrganisationDetails = async () => {
  const { data } = await axios.get(`${API_BASE_URL}/organisation/details`)
  return data
}

// Credits spent per organisation between two dates (YYYY-MM-DD, inclusive):
// the admin's own organisation, plus its sub-organisations for a parent's admin.
const fetchOrganisationUsage = async ({ start, end }) => {
  const params = new URLSearchParams({ start, end })
  const { data } = await axios.get(`${API_BASE_URL}/organisation/usage?${params.toString()}`)
  // {start, end, organisations: [{organisation, is_own, credits_spent, charge_count}], ...}
  return Array.isArray(data?.organisations) ? data.organisations : []
}

const fetchOrganisationLogs = async () => {
  const { data } = await axios.get(`${API_BASE_URL}/logs/`)
  return data.results || []
}

const fetchLatestLogs = async () => {
  const { data } = await axios.get(`${API_BASE_URL}/logs/latest_logs/`)
  return data
}

const fetchNotifications = async () => {
  const { data } = await axios.get(`${API_BASE_URL}/notifications/unread_logs/`)
  return data
}

const markNotificationsAsRead = async (lastReadLogId) => {
  const { data } = await axios.post(`${API_BASE_URL}/notifications/mark_as_read/`, {
    last_read_log_id: lastReadLogId,
  })
  return data
}

export const useUserList = () => {
  return useQuery(['user-list'], () => fetchUserList())
}

export const useOrganisationDetails = () => {
  return useQuery(['organisation-details'], () => fetchOrganisationDetails())
}

export const useOrganisationUsage = ({ start, end, enabled = true }) => {
  return useQuery(['organisation-usage', start, end], () => fetchOrganisationUsage({ start, end }), {
    enabled: enabled && Boolean(start && end),
    keepPreviousData: true,
  })
}

export const useOrganisationLogs = () => {
  return useQuery(['organisation-logs'], () => fetchOrganisationLogs())
}

export const useNotifications = () => {
  const { data: unreadLogs = [] } = useQuery(['unread-notifications'], () => fetchNotifications(), {
    refetchInterval: 15000, // Refetch every 15 seconds
  })

  const { data: latestLogs = [] } = useQuery(['latest-logs'], () => fetchLatestLogs(), {
    refetchInterval: 15000, // Refetch every 15 seconds
  })

  return {
    unreadLogs,
    latestLogs,
    allCount: latestLogs.length,
    unreadCount: unreadLogs.length,
  }
}

export const useMarkNotificationsAsRead = () => {
  return useMutation(markNotificationsAsRead)
}
